import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { getTransactionById } from '../services/transactions';

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};

export const TransactionViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        const data = await getTransactionById(id);
        // Handle different response structures
        const txData = Array.isArray(data) ? data[0] : (data.transaction || data);
        setTransaction(txData);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load transaction');
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [id]);

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading transaction...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl">
          <div className="mb-4">
            <Button variant="secondary" onClick={() => navigate('/transactions')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Transactions
            </Button>
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (!transaction) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl">
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Transaction not found</p>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl px-4 sm:px-6 lg:px-0">
        <div className="mb-6">
          <Button variant="secondary" onClick={() => navigate('/transactions')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Transactions
          </Button>
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Transaction Details</h1>

        <Card>
          <div className="space-y-6">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Date</label>
              <p className="text-lg text-gray-900 dark:text-white">{formatDate(transaction.created_at || transaction.date)}</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Description</label>
              <p className="text-lg text-gray-900 dark:text-white">{transaction.description}</p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Category</label>
              <p className="text-lg text-gray-900 dark:text-white">{transaction.category}</p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Amount</label>
              <p className={`text-lg font-medium ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                {transaction.type === 'income' ? '+' : '-'}₦{parseFloat(transaction.amount).toFixed(2)}
              </p>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Type</label>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                transaction.type === 'income' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
              </span>
            </div>

            {/* ID */}
            <div className="border-t dark:border-gray-600 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Transaction ID</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{transaction.id}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t dark:border-gray-600">
              <Button 
                variant="primary" 
                onClick={() => navigate(`/transactions/${id}/edit`)}
              >
                Edit Transaction
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/transactions')}
              >
                Close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
};
